import React, { useCallback, useMemo, useReducer, useState } from 'react'
import PageLayout from '../../../components/layout/PageLayout'
import onImagePasted from '../../../hooks/etc/onImagePasted'
import WizButton from '../../../components/button/WizButton'
import AnnouncementMenuView from '../../../features/page/post/announcement/AnnouncementMenuView'
import AnnouncementInputView from '../../../features/page/post/announcement/AnnouncementInputView'
import AnnouncementMarkdownView from '../../../features/page/post/announcement/AnnouncementMarkdownView'
import Text from '../../../components/text/Text'
import AnnouncementPreview from '../../../features/page/post/announcement/AnnouncementPreview'
import { postCreateAnnounce } from '../../../apis/announce/createAnnounce'
import WizSpinner from '../../../components/spinner/WizSpinner'
import { useNavigate } from 'react-router-dom'
import WizImage from '../../../components/image/WizImage'
import useQueryUserDetail from '../../../hooks/api/user/useQueryUserDetail'

export type AnnounceContensType = {
    title?: string
    content?: string
}
export type AnnounceImageType = {
    markdown?: string
    url?: string
}
export const DEFAULT_TITLE_EXECUTE_STRING = '># '

export type AnnouncementReduceState = {
    value?: string
    categories?: string[]
    images?: AnnounceImageType[]
    channel: string
    visibility: number
    thumbnail?: string
    important_at?: number
    pin_priority?: number
    upload_at?: number
    created_at?: number
    notify?: boolean
    notify_expires_at?: number
}
export const initState: AnnouncementReduceState = {
    value: DEFAULT_TITLE_EXECUTE_STRING,
    categories: [],
    images: [],
    channel: 'ko',
    visibility: 1,
    thumbnail: '',
    pin_priority: 0,
    notify: false,
}

function AnnounceCreate() {
    const { state, reset, setValue, setCategories, setImages, setChannel, setVisibility, setThumbnail, setImportantAt, setPinPriority, setUploadAt, setNotify, setNotifyExpiresAt } =
        useAnnouncementReduce(initState)
    const { value, categories, images, channel, visibility, thumbnail, important_at, pin_priority, upload_at, notify, notify_expires_at }: AnnouncementReduceState = state

    const navigation = useNavigate()

    const [isLoading, setIsLoading] = useState(false)

    const { data, refetch } = useQueryUserDetail(localStorage?.getItem('wiz-admin-userId'))

    const firstImage = useMemo(() => {
        let splits = value.split('\n')
        const imageArray = splits
            .map(e => e?.match(/!\[[^\]]*\]\((.*?)\s*("(?:.*[^"])")?\s*\)/g)?.shift())
            .map(e => e?.match(/\b(https?:\/\/.*?\.[a-z]{2,4}\/[^\s]*\b)/g)?.shift())
            .filter(element => element)
        if (imageArray && imageArray.length > 0) {
            return imageArray[0].replace('?action=fit', '')
        }
        return ''
    }, [value])

    const announceContents: AnnounceContensType = useMemo(() => {
        let splits = value.split('\n')
        let title: string
        let content: string

        const firstLine = splits[0]
        const hasTitle = firstLine.indexOf(DEFAULT_TITLE_EXECUTE_STRING)
        if (hasTitle !== -1) {
            title = firstLine.replace(DEFAULT_TITLE_EXECUTE_STRING, '')
            content = value.replace(firstLine, '').slice(1)
        } else {
            content = value
        }
        return { title: title, content: content }
    }, [value])

    const handleMarkdownImage = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        const newImage = await onImagePasted(event.dataTransfer, setValue)
        setImages([...images, ...newImage])
    }

    const onClickCreatePost = useCallback(async () => {
        const { data, error } = await postCreateAnnounce({
            type: 'announce',
            title: announceContents.title,
            content: announceContents.content,
            channel: channel,
            visibility: visibility,
            images: images.length > 0 ? images.map(e => e.url) : [thumbnail || firstImage],
            categories: categories,
            thumbnail: thumbnail || firstImage,
            important_at: important_at,
            pin_priority: pin_priority,
            upload_at: upload_at,
            notify: notify,
            notify_expires_at: notify_expires_at,
        })
        if (data) {
            reset()
            setIsLoading(false)
            alert('공지 글이 생성되었습니다.')
            navigation(`/announce`)
        }
        if (error) {
            setIsLoading(false)
            return alert('공지 글 생성에 실패했습니다.')
        }
    }, [
        isLoading,
        announceContents,
        channel,
        visibility,
        images,
        categories,
        thumbnail,
        important_at,
        pin_priority,
        upload_at,
        notify,
        notify_expires_at,
        firstImage,
    ])

    const onClickAddTag = (category: string) => {
        switch (category) {
            case 'Official':
                category = 'announcement'
                break
            case 'Event':
                category = 'event'
                break
            case 'Media':
                category = 'media'
                break
        }
        const list = Array.from(new Set([...categories, category]))
        setCategories(list)
    }
    const onClickRemoveCategory = (category: string) => {
        categories.splice(categories.indexOf(category), 1)
        setCategories([...categories])
    }
    const onClickRemoveImportantAt = () => {
        setImportantAt(undefined)
    }

    const onAddThumbnail = (thumbnail?: string) => {
        const newThunmbnail = thumbnail.replace('?action=fit', '')
        setThumbnail(newThunmbnail)
    }

    return (
        <PageLayout title={'Create Announcement'}>
            <div data-color-mode="light">
                <AnnouncementMenuView
                    channel={channel}
                    setChannel={setChannel}
                    visibility={visibility}
                    setVisibility={setVisibility}
                    importantAt={important_at}
                    setImportantAt={setImportantAt}
                    pinPriority={pin_priority}
                    setPinPriority={setPinPriority}
                    uploadAt={upload_at}
                    setUploadAt={setUploadAt}
                    thumbnail={thumbnail}
                    setThumbnail={onAddThumbnail}
                    notify={notify}
                    setNotify={setNotify}
                    notifyExpiresAt={notify_expires_at}
                    setNotifyExpiresAt={setNotifyExpiresAt}
                    onClickAddTag={onClickAddTag}>
                    <div>
                        <Text className={'text-ti3'}>Now Login Account</Text>
                        <div
                            className={
                                'flex items-center gap-x-[5px] bg-white mt-[7px] px-[30px] py-[10px] border border-line02 rounded-[5px] text-center w-fit'
                            }>
                            <WizImage
                                src={`${data?.thumbnail}?type=webp&width=1000&quality=100`}
                                className={'w-[36px] h-[36px] rounded-full'}
                            />
                            <Text className={'text-ti2'}>{data?.name}</Text>
                        </div>
                    </div>
                </AnnouncementMenuView>

                <div className={'grid grid-cols-2 gap-[20px] min-h-full md:grid-cols-1 md:mt-[30px]'}>
                    <AnnouncementInputView
                        value={value}
                        setValue={setValue}
                        handleMarkdownImage={handleMarkdownImage}
                    />
                    <div className={'space-y-[10px]'}>
                        <Text className={'text-h3'}>Preview</Text>
                        <AnnouncementPreview
                            categories={categories}
                            announceContents={announceContents}
                            importantAt={important_at}
                            thumbnail={thumbnail || firstImage}
                            onClickRemoveCategory={onClickRemoveCategory}
                            onClickRemoveImportantAt={onClickRemoveImportantAt}
                        />
                        <Text className={'text-h3'}>Detail</Text>
                        <AnnouncementMarkdownView
                            categories={categories}
                            announceContents={announceContents}
                            importantAt={important_at}
                            onClickRemoveCategory={onClickRemoveCategory}
                            onClickRemoveImportantAt={onClickRemoveImportantAt}
                        />
                    </div>
                </div>
            </div>
            <div className={'text-center mt-[20px]'}>
                {!isLoading ? (
                    <WizButton
                        text={'Create Announcement'}
                        size={'medium'}
                        onClick={() => {
                            setIsLoading(true)
                            onClickCreatePost()
                        }}
                    />
                ) : (
                    <WizSpinner />
                )}
            </div>
        </PageLayout>
    )
}

function reducer(state: any, action: any) {
    switch (action.type) {
        case 'field_update':
            return { ...state, [action.field]: action.value }
        case 'reset':
            return action.payload
        default:
            return { ...state }
    }
}

export function useAnnouncementReduce(initialState: AnnouncementReduceState) {
    const [state, dispatch] = useReducer(reducer, initialState)
    const reset = () => {
        dispatch({ type: 'reset', payload: initialState })
    }
    const setValue = (value: string) => {
        dispatch({ type: 'field_update', field: 'value', value: value })
    }
    const setCategories = (categories: string[]) => {
        dispatch({ type: 'field_update', field: 'categories', value: categories })
    }
    const setImages = (images: AnnounceImageType[]) => {
        dispatch({ type: 'field_update', field: 'images', value: images })
    }
    const setChannel = (channel: string) => {
        dispatch({ type: 'field_update', field: 'channel', value: channel })
    }
    const setVisibility = (visibility: number) => {
        dispatch({ type: 'field_update', field: 'visibility', value: visibility })
    }
    const setThumbnail = (thumbnail: string) => {
        dispatch({ type: 'field_update', field: 'thumbnail', value: thumbnail })
    }
    const setImportantAt = (important_at: number) => {
        dispatch({ type: 'field_update', field: 'important_at', value: important_at })
    }
    const setPinPriority = (pin_priority: number) => {
        dispatch({ type: 'field_update', field: 'pin_priority', value: pin_priority })
    }
    const setUploadAt = (upload_at: number) => {
        dispatch({ type: 'field_update', field: 'upload_at', value: upload_at })
    }
    const setNotify = (notify: boolean) => {
        dispatch({ type: 'field_update', field: 'notify', value: notify })
    }
    const setNotifyExpiresAt = (notify_expires_at: number) => {
        dispatch({ type: 'field_update', field: 'notify_expires_at', value: notify_expires_at })
    }

    return {
        state,
        dispatch,
        reset,
        setValue,
        setCategories,
        setImages,
        setChannel,
        setVisibility,
        setThumbnail,
        setImportantAt,
        setPinPriority,
        setUploadAt,
        setNotify,
        setNotifyExpiresAt,
    }
}

export default AnnounceCreate
